import { FunctionComponent, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';
import { FlexContainer } from '@/components/Layout/Container';
import { LoadingButton } from '@mui/lab';
import { usePostTicketCancellationMutation } from '@/api/v2/tickets';
import { useNotification } from '@starjack/features/notifications';

import { Ticket } from '@/models/ticket';
import { TicketDrawerLabel } from '@/components/TicketsOverviewUI/TicketDrawerContent/TicketDrawerLabel';
import { formatDateToString } from '@/components/TicketsOverviewUI/ticketUiUtils';

type ConfirmationDialogConfig = {
    open: boolean;
    type: 'refund' | 'cancel';
};

export const TicketRefundBlock: FunctionComponent<{ ticket: Ticket }> = ({
    ticket,
}) => {
    const [confirmationDialogConfig, setConfirmationDialog] =
        useState<ConfirmationDialogConfig>({ open: false, type: 'refund' });

    const [postCancelTicket, { isLoading: isLoading }] =
        usePostTicketCancellationMutation();
    const { addNotification } = useNotification();

    const onDeleteTicket = () => {
        postCancelTicket({
            ticketId: ticket.id,
            refund: confirmationDialogConfig.type === 'refund',
        })
            .unwrap()
            .then(() => {
                addNotification({
                    message: `Ticket erfolgreich ${
                        confirmationDialogConfig.type === 'refund'
                            ? 'refundiert'
                            : 'storniert'
                    }`,
                    type: 'success',
                });
                setConfirmationDialog((prevState) => ({
                    ...prevState,
                    open: false,
                }));
            })
            .catch(() => {
                addNotification({
                    message: `Fehler beim ${
                        confirmationDialogConfig.type === 'refund'
                            ? 'Refundieren'
                            : 'Stornieren'
                    }, bitte versuchen Sie es später noch einmal.`,
                    type: 'error',
                });
            });
    };

    const isCancelable =
        ticket.cancelable && !ticket.cancelledBy && !ticket.cancellationDate;

    const notCancelableString = useMemo(() => {
        if (ticket.cancelledBy || ticket.cancellationDate) {
            return (
                <Box
                    sx={{
                        width: '100%',
                        textAlign: 'center',
                    }}
                >
                    <TicketDrawerLabel>
                        Ticket bereits storniert
                    </TicketDrawerLabel>

                    <TicketDrawerLabel>
                        Storniert von: <strong>{ticket.cancelledBy}</strong>
                    </TicketDrawerLabel>
                    <TicketDrawerLabel>
                        Storniert am:{' '}
                        <strong>
                            {formatDateToString(ticket.cancellationDate, {
                                detailed: true,
                            })}
                        </strong>
                    </TicketDrawerLabel>
                </Box>
            );
        } else if (!ticket.cancelable) {
            return (
                <TicketDrawerLabel>Ticket nicht stornierbar</TicketDrawerLabel>
            );
        }
        return '';
    }, [ticket.cancelable, ticket.cancelledBy, ticket.cancellationDate]);

    return (
        <Box>
            <FlexContainer
                sx={{
                    gap: 2,
                }}
                center
            >
                {!isCancelable ? (
                    <TicketDrawerLabel
                        sx={{
                            fontWeight: 600,
                        }}
                    >
                        {notCancelableString}
                    </TicketDrawerLabel>
                ) : (
                    <>
                        <Button
                            onClick={() => {
                                setConfirmationDialog(() => ({
                                    open: true,
                                    type: 'refund',
                                }));
                            }}
                            variant='contained'
                            color='error'
                        >
                            Ticket Refundieren
                        </Button>

                        <Button
                            onClick={() => {
                                setConfirmationDialog(() => ({
                                    open: true,
                                    type: 'cancel',
                                }));
                            }}
                            variant='contained'
                            color='error'
                        >
                            Ticket Stornieren
                        </Button>
                    </>
                )}
            </FlexContainer>

            <Dialog
                onClose={() => {
                    setConfirmationDialog((prevState) => ({
                        ...prevState,
                        open: false,
                    }));
                }}
                open={confirmationDialogConfig.open}
            >
                <DialogTitle>
                    Ticket{' '}
                    {confirmationDialogConfig.type === 'refund'
                        ? 'refundieren '
                        : 'stornieren'}
                </DialogTitle>

                <DialogContent>
                    Wollen Sie das Ticket wirklich{' '}
                    {confirmationDialogConfig.type === 'refund'
                        ? 'refundieren '
                        : 'stornieren'}
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() => {
                            setConfirmationDialog((prevState) => ({
                                ...prevState,
                                open: false,
                            }));
                        }}
                        variant='outlined'
                    >
                        Abbrechen
                    </Button>
                    <LoadingButton
                        loading={isLoading}
                        onClick={onDeleteTicket}
                        color='error'
                        variant='contained'
                    >
                        {confirmationDialogConfig.type === 'refund'
                            ? 'Refundieren'
                            : 'Stornieren'}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
